import * as React from 'react';
import { changeLocation } from '@src/core/store';
import useTranslate, { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import { Order, Cart, CartItem, Sale, SellingMode } from '@src/core/buyerApiClient';
import { useAppSelector, useAppDispatch, useWidgetParam } from '@src/core/hooks';
import { appInfoSelector } from '@src/stores/app';
import { pendingOrdersSelector, fetchOrderRequiredFormInputs } from '@src/features/orders';
import { ClassNamespace, formatCents, formatDate, formatMinutes, extractValues } from '@src/common';
import {
  removeCartItem,
  setCartCoupon,
  removeCartCoupon,
  completeCartSelector,
  cartSetCouponErrorsSelector,
} from '@src/features/cart/store/cart';
import type { CouponErrorsType } from '@src/features/coupons/core/couponErrors';
import '@src/components/style/CartDisplay.scss';
import { track } from '@src/services/analytics';

interface CartDisplayProps {
  cn: ClassNamespace;
  proceedToNextStage: () => void;
  editable?: boolean;
}
interface CartItemProps {
  cn: ClassNamespace;
  item: CartItem;
  removeCartItem: () => void;
  editable?: boolean;
}
interface CartCouponProps {
  cn: ClassNamespace;
  cart: Cart;
  removeCoupon: () => void;
  editable?: boolean;
}
interface CouponFormProps {
  cn: ClassNamespace;
  showForm: boolean;
  setShowForm: (arg0: boolean) => void;
  setCartCoupon: (arg0: string) => void;
  errors?: CouponErrorsType[];
}

export const CartDisplay = (props: CartDisplayProps) => {
  const { cn, proceedToNextStage, editable = true } = props;

  const continueShoppingPage = useWidgetParam('continue-shopping-page');
  const continueShoppingTxt = useWidgetParam('continue-shopping-text');
  const proceedSaleTxt = useWidgetParam('proceed-sale-text');
  const dispatch = useAppDispatch();
  const { cart, isLoading, hasErrors, hasCheckoutableItems } = useAppSelector(completeCartSelector);
  const appInfo = useAppSelector(appInfoSelector);
  const pendingOrders = useAppSelector(pendingOrdersSelector);
  const couponErrors = useAppSelector(cartSetCouponErrorsSelector);
  const [showCouponForm, setShowCouponForm] = React.useState(false);
  const { i18nElement } = useTranslate();
  const cartHeaders: DefaultMessagesKeys[] = ['base_item', 'base_date', 'base_description', 'base_value'];

  const handleContinueShopping = () => dispatch(changeLocation(continueShoppingPage!));
  const handleSaleSelection = (order: Order) => dispatch(fetchOrderRequiredFormInputs(order));
  const onItemRemove = (item: CartItem) => dispatch(removeCartItem(item));
  const setCoupon = (code: string) => dispatch(setCartCoupon(code));
  const removeCoupon = () => dispatch(removeCartCoupon());

  const handleRemoveCartItem = (item: CartItem) => {
    track('removeCartItem', { item });

    onItemRemove(item);
  };

  return (
    <div className={cn('cart-display')}>
      <div className={cn('pending-sales')}>
        {pendingOrders && pendingOrders.length > 0 ? (
          <div>
            <h4>{i18nElement('checkout_pendingSales')}</h4>
            {pendingOrders.map((order) => (
              <a key={order.id} onClick={() => handleSaleSelection(order)}>
                {i18nElement('checkout_saleNumber', {
                  saleNumber: order.code,
                })}
              </a>
            ))}
          </div>
        ) : null}
      </div>
      <div className={cn('cart-item-list')}>
        {cart && !isLoading ? (
          <>
            <div className={cn('cart-items-holder')}>
              {cart.items.length > 0 ? (
                <>
                  <div className={cn('cart-row', 'cart-row-header')}>
                    {cartHeaders.map((key) => (
                      <strong key={key} className={cn('cart-col')}>
                        {i18nElement(key)}
                      </strong>
                    ))}
                  </div>
                  {cart.items.map((item) => (
                    <div key={item.id} className={cn('cart-row', 'cart-item')}>
                      <CartItemComponent
                        cn={cn}
                        item={item}
                        removeCartItem={() => handleRemoveCartItem(item)}
                        editable={editable}
                      />
                    </div>
                  ))}
                  {cart.coupon ? (
                    <CartCoupon cn={cn} cart={cart} removeCoupon={removeCoupon} editable={editable} />
                  ) : null}
                  <div className={cn('cart-row', 'cart-value')}>
                    <div className={cn('cart-col')}>
                      {cart.coupon || (appInfo && !appInfo.hasCouponAvailable) ? null : (
                        <CouponForm
                          cn={cn}
                          showForm={showCouponForm}
                          setShowForm={setShowCouponForm}
                          setCartCoupon={setCoupon}
                          errors={couponErrors}
                        />
                      )}
                    </div>
                    <div className={cn('cart-col', 'cart-empty-col')} />
                    <div className={cn('cart-col', 'cart-value-col', 'featured-label')}>
                      <strong>{i18nElement('checkout_totalValue')}</strong>
                    </div>
                    <div className={cn('cart-col', 'cart-value-col')}>
                      {formatCents(cart.costPreview.totalAmountCents)}
                    </div>
                  </div>
                </>
              ) : (
                <div className={cn('empty-cart-notice')}>{i18nElement('checkout_yourCartIsEmpty')}</div>
              )}
            </div>
            {cart.items.length > 0 && cart.generalErrors ? (
              <div className={cn('cart-errors')}>
                <strong>{i18nElement('checkout_yourCartErrors')}</strong>
                {cart.generalErrors.map((err) => (
                  <div key={err.path}>{err.path + ' ' + err.type}</div>
                ))}
              </div>
            ) : null}
            <div className={cn('cart-actions')}>
              {continueShoppingPage ? (
                <button type='button' className='planne-button planne-border-button' onClick={handleContinueShopping}>
                  {continueShoppingTxt ?? i18nElement('checkout_continueShopping')}
                </button>
              ) : null}
              {hasCheckoutableItems ? (
                <button
                  data-cy='finishPurchase'
                  className='planne-button'
                  disabled={hasErrors}
                  onClick={proceedToNextStage}
                >
                  {proceedSaleTxt ?? i18nElement('checkout_finishSale')}
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <div className={cn('loading-message')}>{i18nElement('base_loading')}</div>
        )}
      </div>
    </div>
  );
};

const CartItemComponent = (props: CartItemProps) => {
  const { cn, item, removeCartItem, editable = true } = props;
  const { i18nElement, i18n } = useTranslate();

  return (
    <>
      <div className={cn('title', 'cart-col')}>
        {item.product.thumbnailUrl ? <img className={cn('thumbnail')} src={item.product.thumbnailUrl} /> : null}
        <span className={cn('product-name')}>{item.product.name}</span>
        {item.resources && item.resources.length ? (
          <div className={cn('resources')}>
            <strong>{i18nElement('checkout_placement')}</strong>
            {item.resources.map((res) => (
              <div key={res.resource.name}>
                <span>{res.resource.name}</span>
              </div>
            ))}
            {item.costPreview.resourcePriceCents > 0 ? (
              <span>{`${i18n('checkout_resource_extra_value')} ${formatCents(
                item.costPreview.resourcePriceCents,
              )}`}</span>
            ) : null}
          </div>
        ) : null}
        {item.errors && item.errors.length > 0 ? (
          <div className={cn('cart-validation')}>{i18n('cart_itemErrorWarning')}</div>
        ) : null}
      </div>
      <div className={cn('cart-col')}>{item.scheduleDate ? formatDate(item.scheduleDate) : '-'}</div>
      <div className={cn('description', 'cart-col')}>
        {item.tariffs.map((cartTariff) => {
          let txt = [`${cartTariff.quantity} ${i18n('checkout_units')}`, formatCents(cartTariff.tariff.priceCents)];
          if (item.scheduleTimeMinutes != null) {
            txt = [formatMinutes(item.scheduleTimeMinutes!), ...txt];
          }

          return (
            <span key={cartTariff.tariff.id} className={cn('tariff')}>
              <strong>{cartTariff.tariff.type.name}</strong>
              <span>{txt.join(' - ')}</span>
            </span>
          );
        })}
        {item.additionals.length > 0 ? (
          <>
            <strong className={cn('additionals-title')}>{i18nElement('checkout_additionals')}</strong>
            {item.additionals.map((cartItemAdditional) => (
              <div
                key={cartItemAdditional.additional.id}
              >{`${cartItemAdditional.additional.title} ${cartItemAdditional.formattedAmount}`}</div>
            ))}
          </>
        ) : null}
      </div>
      <div className={cn('cart-col', 'cart-item-price-cents')}>{formatCents(item.costPreview.totalPriceCents)}</div>
      {editable ? (
        <div className={cn('cart-col', 'cart-item-actions')}>
          <a className={cn('remove-item')} title={i18n('checkout_remove')} onClick={removeCartItem}>
            <span
              dangerouslySetInnerHTML={{
                __html: `&#128465; ${i18n('checkout_remove')}`,
              }}
            />
          </a>
        </div>
      ) : null}
    </>
  );
};

const CartCoupon = (props: CartCouponProps) => {
  const { cn, cart, removeCoupon, editable = true } = props;

  const { i18nElement } = useTranslate();
  return (
    <div className={cn('cart-row', 'cart-value')}>
      <div className={cn('cart-col', 'cart-empty-col')} />
      <div className={cn('cart-col', 'cart-empty-col')} />
      <div className={cn('cart-col')}>
        <span>Cupom </span>
        <strong>{cart.coupon!.code}</strong>
        <div>{`Desconto: ${cart.coupon!.formattedDiscount}`}</div>
        <div>
          {cart.coupon!.invalid ? (
            <span className={cn('cart-validation')}>{i18nElement('checkout_invalidCoupon')}</span>
          ) : null}
          {editable ? (
            <button className='planne-button planne-border-button planne-small-button' onClick={removeCoupon}>
              {i18nElement('checkout_removeCoupon')}
            </button>
          ) : null}
        </div>
      </div>
      <div className={cn('cart-col')}>
        <span className='planne-line-through'>{formatCents(cart.costPreview.fullPartialAmountCents)}</span>
        <span>{' ' + formatCents(cart.costPreview.totalAmountCents)}</span>
      </div>
    </div>
  );
};

const CouponForm = ({ cn, showForm, setShowForm, setCartCoupon, errors }: CouponFormProps) => {
  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const { code } = extractValues(ev.target as HTMLFormElement);
    setCartCoupon(code);
  };
  const { i18nElement, i18n } = useTranslate();

  const errorMessage = errors?.map((key) => i18n(key as DefaultMessagesKeys)).join('. ');

  return (
    <div>
      {showForm ? (
        <form className={cn('cart-add-coupon')} onSubmit={handleSubmit}>
          <div>
            <input type='text' name='code' placeholder={i18n('checkout_couponCode')} />
            <button type='submit' className='planne-button planne-border-button planne-small-button'>
              {i18nElement('base_add')}
            </button>
          </div>
          {errorMessage ? (
            <div>
              <span className={cn('cart-validation')}>{errorMessage}</span>
            </div>
          ) : null}
        </form>
      ) : (
        <div className={cn('cart-add-coupon')}>
          <button className='planne-button planne-border-button planne-small-button' onClick={() => setShowForm(true)}>
            {i18nElement('checkout_addCoupon')}
          </button>
        </div>
      )}
    </div>
  );
};
